import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { PROJECTS, PROJECT_CATEGORIES } from "../data/content";
import { Reveal, PageHeader, QuoteCTA } from "../components/Shared";

export default function Portfolio() {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const shown = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <div data-testid="portfolio-page">
      <PageHeader
        tag="Our Work / Portfolio"
        title={<>Proof, Not<br />Promises.</>}
        subtitle="A selection of recent signage, display, and graphics projects. Real photography from your projects can replace these placeholders anytime."
      />

      <section className="pb-24">
        <div className="msg-container">
          <div className="flex flex-wrap gap-3 mb-12" data-testid="portfolio-filters">
            {PROJECT_CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`filter-pill ${filter === c ? "active" : ""}`}
                data-testid={`portfolio-filter-${c.toLowerCase()}`}
              >
                {c}
              </button>
            ))}
          </div>

          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {shown.map((p) => (
                <motion.button
                  layout
                  key={p.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.35 }}
                  onClick={() => setActive(p)}
                  className="msg-card text-left group"
                  data-testid={`portfolio-card-${p.id}`}
                >
                  <div className="img-frame aspect-[4/3] relative">
                    <img src={p.image} alt={p.title} loading="lazy" />
                    <span className="absolute bottom-4 left-4 z-10 font-mono text-[10px] tracking-widest uppercase px-3 py-1.5" style={{ background: "var(--brand-lime)", color: "#0A0A0C" }}>{p.category}</span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display font-bold uppercase text-lg">{p.title}</h3>
                    <p className="text-sm mt-2" style={{ color: "var(--text-3)" }}>{p.client}</p>
                  </div>
                </motion.button>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)" }}
            onClick={() => setActive(null)}
            data-testid="portfolio-modal-overlay"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              style={{ background: "var(--bg-card)", border: "1px solid var(--line)" }}
              onClick={(e) => e.stopPropagation()}
              data-testid="portfolio-modal"
            >
              <div className="img-frame aspect-video relative">
                <img src={active.image} alt={active.title} />
                <button
                  onClick={() => setActive(null)}
                  className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center"
                  style={{ background: "var(--brand-lime)", color: "#0A0A0C" }}
                  data-testid="portfolio-modal-close"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="p-8">
                <span className="font-mono text-[10px] tracking-widest uppercase px-3 py-1.5" style={{ background: "var(--brand-lime)", color: "#0A0A0C" }}>{active.category}</span>
                <h3 className="font-display font-black uppercase text-2xl mt-4">{active.title}</h3>
                <p className="text-sm mt-1" style={{ color: "var(--text-3)" }}>{active.client}</p>
                <p className="mt-4 leading-relaxed" style={{ color: "var(--text-2)" }}>{active.desc}</p>
                <h4 className="font-mono text-xs tracking-[0.2em] uppercase mt-6 mb-3" style={{ color: "var(--text-3)" }}>Technical Specs</h4>
                <ul className="space-y-2">
                  {active.specs.map((sp, i) => (
                    <li key={sp} className="flex items-center gap-3 text-sm">
                      <span className="w-2 h-2" style={{ background: ["var(--brand-orange)", "var(--brand-magenta)", "var(--brand-cyan)"][i % 3] }} />
                      {sp}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <QuoteCTA />
    </div>
  );
}
